import React, { useState } from 'react';
import cn from 'classnames';
import compact from 'lodash/compact';
import { RichTextViewer as QuillRichTextViewer, TextButton, Typography } from 'seagull';
import { ReceiptText } from 'seagull/icons';
import { Collapse } from '@material-ui/core';
import { netTermsToString } from "../../../infra/utils";
import { make as RichTextViewer } from "../../../RichTextViewer.bs";
import { useGlobalContext, useIoContext, useStoreActions } from "../../../contexts";
import { ResponsiveModal } from "../../components/ResponsiveModal";
import { useResponsive } from "../../contexts";
import { approvalMethodNoticePeriodModel } from "../../../models";
import { legalTermsModel, relationshipModel } from "../../../models";
import { requestAccountantAccessModel } from "../../../models/request-accountant-access";
import styles from "./AgreementTerms.module.scss";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
const TitleTypography = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Typography, {
    weight: "bold",
    as: "span",
    className: styles.titleTypography
  }, children);
};
export function AgreementTerms(props) {
  const {
    relationship
  } = props;
  const globalContext = useGlobalContext();
  const {
    api
  } = useStoreActions();
  const {
    isMobile,
    isDesktop
  } = useResponsive();
  const {
    CUSTOM_BRANDING,
    AUTO_APPROVE_INVOICE
  } = useFeatureFlags();
  const [tocVisible, setTocVisible] = useState(false);
  const {
    openNewTab
  } = useIoContext();
  const amendmentsApprovalMethodDescription = approvalMethodNoticePeriodModel.amendmentsDescription(relationship.amendmentsApprovalMethod, AUTO_APPROVE_INVOICE);
  const invoicesApprovalMethodDescription = approvalMethodNoticePeriodModel.invoicesCOBDescription(relationship.invoicesApprovalMethod);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, /*#__PURE__*/React.createElement("section", {
    className: styles.section
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.sectionTitle, {
      [styles.mobile]: isMobile
    })
  }, !CUSTOM_BRANDING && /*#__PURE__*/React.createElement(ReceiptText, null), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    weight: "normal"
  }, "Agreement Terms")), /*#__PURE__*/React.createElement("ul", null, compact([{
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleTypography, null, "Payment terms: "), netTermsToString(relationship.netTerms)),
    info: 'Number of days the payment is due after the invoice date'
  }, {
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleTypography, null, "Effective date: "), relationshipModel.formatEffectiveDate(relationship)),
    info: 'The services start date'
  }, approvalMethodNoticePeriodModel.isAutoApproval(relationship.amendmentsApprovalMethod) ? {
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleTypography, null, "Amendments: "), amendmentsApprovalMethodDescription),
    info: 'Access to Anchor will be available upon approval of agreement. If an invoice associated with the amendment is issued before the end of the notice period, it will require approval.'
  } : {
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleTypography, null, "Amendments: "), amendmentsApprovalMethodDescription)
  }, AUTO_APPROVE_INVOICE && approvalMethodNoticePeriodModel.isAutoApproval(relationship.invoicesApprovalMethod) && {
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleTypography, null, "Out of scope charges: "), invoicesApprovalMethodDescription)
  }, requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess) && {
    text: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "accountant-access-information"
    }, /*#__PURE__*/React.createElement(TitleTypography, null, "Accountant Access: "), "will allow the ".concat(relationship.vendor.name, " team to view your account information on Anchor, including invoices, agreements, and payments, as well as manage your payment methods. "))
  }, {
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "additional-terms-of-service"
    }, /*#__PURE__*/React.createElement(TitleTypography, null, "Additional terms of service: "), /*#__PURE__*/React.createElement(TextButton, {
      "data-testid": "additional-terms-of-service-btn",
      onClick: legalTermsModel.isPDF(relationship.legalTerms) ? async () => {
        const pdfViewUrl = await api.business.getLegalTermsDownloadURL(relationship.legalTerms.pdfUniqueName, relationship.vendor.id);
        if (pdfViewUrl) {
          openNewTab(pdfViewUrl);
        }
      } : isMobile ? () => {
        globalContext.showModal({
          content: _ref2 => {
            let {
              dismissModal
            } = _ref2;
            return /*#__PURE__*/React.createElement(ResponsiveModal, {
              backTitle: "Back to agreement",
              title: "Terms and conditions",
              isDesktop: isDesktop,
              onClose: dismissModal
            }, /*#__PURE__*/React.createElement(RichTextViewer, {
              value: relationship.legalTerms.richTextContent
            }));
          }
        });
      } : () => {
        setTocVisible(!tocVisible);
      }
    }, "Read more")), isDesktop && /*#__PURE__*/React.createElement(Collapse, {
      in: tocVisible
    }, legalTermsModel.isRichText(relationship.legalTerms) ? /*#__PURE__*/React.createElement(RichTextViewer, {
      value: relationship.legalTerms.richTextContent
    }) : /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary"
    }, relationship.coverLetter)))
  }, relationship.extraClause && {
    text: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "additional-notes",
      className: styles.noteContainer
    }, /*#__PURE__*/React.createElement(TitleTypography, null, "Agreement notes: "), /*#__PURE__*/React.createElement(QuillRichTextViewer, {
      className: styles.note,
      "data-testid": "extra-clause-rich-text-viewer",
      value: relationship.extraClause
    }))
  }]).map((_ref3, idx) => {
    let {
      text
    } = _ref3;
    return /*#__PURE__*/React.createElement("li", {
      key: idx,
      className: styles.listItem
    }, /*#__PURE__*/React.createElement(Typography, null, text));
  }))));
}